// Лентата с шестте показателя на дашборда.
//
// Подредбата следва живота на сметката: първо какво е ОТКРИТО в момента, после
// какво е ЗАКРИТО за периода и чак след това с какво е платено, колко е средно,
// колко е сторнирано и колко е отстъпено.
//
// „Открити" и „Закрити" се четат ЕДНО ДО ДРУГО, затова са построени еднакво:
// числото е СУМАТА, а броят сметки стои под нея. Различен фокус в двете съседни
// кутийки кара окото да сравнява пари с бройка.
//
// „Открити" е състояние в МОМЕНТА, а не за периода — затова се показва само
// когато периодът включва днес. Иначе до числа от избрания период би застанало
// число от днес и нищо не би обяснявало разликата. Липсва ли, лентата тръгва
// направо от „Закрити" и ширините на колоните се сместват (двата цели литерала
// по-долу — Tailwind чете изходния текст и не вижда име, сглобено на парче).

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import type { DashboardResponse } from "../../../../api/reportsDashboard";
import { FiscalSplit } from "../FiscalSplit";
import { ReportKpiCard } from "../ReportKpiCard";
import { deltaPercent } from "../reportDelta";
import { pluralLabel } from "../reportPlural";
import { useReportLink } from "../useReportLink";

/** Минус знак U+2212 — за отнетите суми (сторно, отстъпки). */
const MINUS = "−";

/** Ширините на шестте колони; без „Открити" първата отпада. */
const GRID_WITH_OPEN =
  "grid-cols-2 md:grid-cols-3 xl:grid-cols-[1.15fr_1.35fr_1.15fr_1fr_1.15fr_1.15fr]";
const GRID_WITHOUT_OPEN =
  "grid-cols-2 md:grid-cols-3 xl:grid-cols-[1.35fr_1.15fr_1fr_1.15fr_1.15fr]";

type Props = {
  data: DashboardResponse;
  compare: boolean;
  /** Периодът включва днешния ден → „Открити" има смисъл. */
  includesToday: boolean;
  isNarrow: boolean;
};

export const DashboardKpis = ({
  data,
  compare,
  includesToday,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();
  // Всяка съседна справка е зад СВОЕ право: без проверка кутийката води до
  // мигване на URL-а и връщане обратно (`Reports.tsx` пренасочва).
  const openBills = useReportLink("bills-history");
  const openVoids = useReportLink("cancellations");
  const openDiscounts = useReportLink("clients");

  const closed = data.closed_today;
  const sessionAccess = data.session_access ?? true;
  const fiscalized = closed.fiscalized_count ?? 0;
  const unfiscalized = closed.unfiscalized_count ?? 0;
  const previous = compare ? data.previous : null;

  const revenue = parseDecimal(closed.total_amount);
  const avgCheck = parseDecimal(data.today_metrics.avg_check);
  const cash = parseDecimal(closed.cash_amount);
  const card = parseDecimal(closed.card_amount);
  const voids = parseDecimal(data.voids_today.total_sum);
  const discounts = parseDecimal(data.discounts_today.total_sum);
  const itemsQuantity = parseDecimal(data.items_quantity);
  const openCount = data.open_tables.count;
  const openAmount = parseDecimal(data.open_tables.total_amount);

  // Делът в брой/карта се смята от СБОРА на двете, не от оборота: сметка на
  // сметка (метод 4) не влиза в нито едно от двете и иначе процентите не дават 100.
  const paidTotal = cash + card;
  const cashShare = paidTotal > 0 ? Math.round((cash / paidTotal) * 100) : 0;
  const cardShare = paidTotal > 0 ? 100 - cashShare : 0;

  // Брутото = платеното + отстъпките; така „X % от брутото" е делът от това,
  // което сметките щяха да струват без отстъпка.
  const gross = revenue + discounts;
  const discountShare = gross > 0 ? (discounts / gross) * 100 : 0;

  const withDelta = (current: number, prev: number | undefined) =>
    compare && previous ? deltaPercent(current, prev ?? 0) : undefined;

  const closedBillsLabel = pluralLabel(
    t,
    closed.total_count,
    "reportsPage.dashBillsCountOne",
    "reportsPage.dashBillsCount",
  );

  return (
    <div
      className={`grid gap-2.5 ${
        isNarrow
          ? "grid-cols-2"
          : includesToday
            ? GRID_WITH_OPEN
            : GRID_WITHOUT_OPEN
      }`}
    >
      {includesToday ? (
        <ReportKpiCard
          kicker={t("reportsPage.dashOpen")}
          value={fmtEuro(openAmount)}
          sub={
            openCount
              ? pluralLabel(
                  t,
                  openCount,
                  "reportsPage.dashBillsCountOne",
                  "reportsPage.dashBillsCount",
                )
              : t("reportsPage.dashNoOpenTables")
          }
          onClick={openBills ?? undefined}
        />
      ) : null}

      <ReportKpiCard
        kicker={t("reportsPage.dashClosed")}
        value={fmtEuro(revenue)}
        // При сесия с втори код под броя сметки стои разбивката БЕЗ ДУМИ —
        // същият знак както на реда в „Справка сметки", по брой И по сума.
        // Иначе дашбордът само сумира и разликата между двата режима е
        // невидима: човек вижда, че сметките са повече, без да разбира кои
        // са в повечето.
        sub={
          sessionAccess && unfiscalized > 0 ? (
            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
              <span>{closedBillsLabel}</span>
              <FiscalSplit
                fiscalized={fiscalized}
                unfiscalized={unfiscalized}
                fiscalizedAmount={closed.fiscalized_amount}
                unfiscalizedAmount={closed.unfiscalized_amount}
              />
            </span>
          ) : (
            closedBillsLabel
          )
        }
        delta={withDelta(revenue, parseDecimal(previous?.total_amount))}
        hero
        onClick={openBills ?? undefined}
      />

      <ReportKpiCard
        kicker={t("reportsPage.dashCashCard")}
        value={`${cashShare} / ${cardShare} %`}
        sub={`${fmtEuro(cash)} · ${fmtEuro(card)}`}
      />

      <ReportKpiCard
        kicker={t("reportsPage.dashAvgCheck")}
        value={fmtEuro(avgCheck)}
        sub={pluralLabel(
          t,
          Math.round(itemsQuantity),
          "reportsPage.dashItemsCountOne",
          "reportsPage.dashItemsCount",
        )}
        delta={withDelta(avgCheck, parseDecimal(previous?.avg_check))}
      />

      <ReportKpiCard
        kicker={t("reportsPage.dashVoids")}
        value={voids > 0 ? `${MINUS}${fmtEuro(voids)}` : fmtEuro(0)}
        sub={t("reportsPage.dashVoidsCount", {
          count: data.voids_today.count,
        })}
        delta={withDelta(voids, parseDecimal(previous?.voids_total))}
        invertDelta
        hot={voids > 0}
        onClick={openVoids ?? undefined}
      />

      <ReportKpiCard
        kicker={t("reportsPage.dashDiscounts")}
        value={discounts > 0 ? `${MINUS}${fmtEuro(discounts)}` : fmtEuro(0)}
        sub={pluralLabel(
          t,
          data.discounts_today.bills_count,
          "reportsPage.dashDiscountsSubOne",
          "reportsPage.dashDiscountsSub",
          { share: discountShare.toFixed(1) },
        )}
        delta={withDelta(discounts, parseDecimal(previous?.discounts_total))}
        invertDelta
        onClick={openDiscounts ?? undefined}
      />
    </div>
  );
};
